import React, { useState } from "react";
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  TextField, Divider, Alert,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadIcon from "@mui/icons-material/Upload";

/** Popup de import/export da DEFINIÇÃO do formulário (campos, páginas, lógica, escore). */
export default function ImportExportDialog({ open, onClose, definition, formTitle, formDescription, onImport }) {
  const [text, setText] = useState("");
  const [err, setErr] = useState("");

  function handleExport() {
    // inclui título + descrição do form junto da definição (flat, retrocompatível com JSON antigo)
    const payload = { title: formTitle || "", description: formDescription || "", ...definition };
    const json = JSON.stringify(payload, null, 2);
    const blob = new Blob([json], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    const slug = (formTitle || "formulario").trim().replace(/[^\w-]+/g, "_").toLowerCase() || "formulario";
    a.href = url;
    a.download = `${slug}.json`;
    a.click();
    URL.revokeObjectURL(url);
  }

  function handleFile(e) {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setText(String(reader.result || ""));
    reader.readAsText(file);
  }

  function handleImport() {
    setErr("");
    let parsed;
    try {
      parsed = JSON.parse(text);
    } catch (e) {
      setErr("JSON inválido: " + e.message);
      return;
    }
    if (!parsed || !Array.isArray(parsed.fields) || !Array.isArray(parsed.pages)) {
      setErr("JSON inválido: precisa ter os arrays 'fields' e 'pages'.");
      return;
    }
    // pages vazio passava por Array.isArray: o builder morria em currentPage.fieldIds e o
    // autosave, já disparado, publicava a definição vazia — o formulário perdia tudo e o
    // editor não reabria. O guard existe no caminho de carga e faltava no de import.
    if (parsed.pages.length === 0) {
      setErr("JSON inválido: o formulário precisa de ao menos uma página.");
      return;
    }
    if (!window.confirm("Isso substitui TODA a definição do formulário atual. Continuar?")) return;
    // preenche chaves ausentes com defaults para não quebrar o editor
    // meta do form (título/descrição) — opcional; JSON antigo (só definição) segue funcionando
    const meta = {};
    if (typeof parsed.title === "string") meta.title = parsed.title;
    if (typeof parsed.description === "string") meta.description = parsed.description;
    // Preserva chaves não listadas da definição (ex.: integration.legacyApa).
    // Descartá-las apagava marcadores em silêncio a cada import.
    const rest = { ...parsed };
    delete rest.title;
    delete rest.description;
    onImport({
      ...rest,
      pages: parsed.pages,
      fields: parsed.fields,
      logic: Array.isArray(parsed.logic) ? parsed.logic : [],
      calculations: Array.isArray(parsed.calculations) ? parsed.calculations : [],
      scoring: parsed.scoring || undefined,
      theme: parsed.theme || {},
    }, meta);
    setText("");
    onClose();
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Import / Export (JSON)</DialogTitle>
      <DialogContent dividers>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>Exportar</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Baixa a definição atual do formulário (campos, páginas, lógica, escore e cálculos).
        </Typography>
        <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExport}>
          Baixar JSON
        </Button>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>Importar</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Cole o JSON ou selecione um arquivo. <b>Substitui</b> a definição atual.
        </Typography>
        <Box>
          <Button component="label" size="small" startIcon={<UploadIcon />} sx={{ mb: 1 }}>
            Escolher arquivo
            <input type="file" accept="application/json,.json" hidden onChange={handleFile} />
          </Button>
        </Box>
        <TextField fullWidth multiline minRows={6} placeholder='{ "pages": [...], "fields": [...] }'
          value={text} onChange={(e) => setText(e.target.value)}
          InputProps={{ sx: { fontFamily: "monospace", fontSize: 12 } }} />
        {err && <Alert severity="error" sx={{ mt: 1 }}>{err}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Fechar</Button>
        <Button variant="contained" onClick={handleImport} disabled={!text.trim()}>Importar</Button>
      </DialogActions>
    </Dialog>
  );
}
